import React, { useCallback, useEffect, useState } from "react";
import { AlertTriangle, Check, X } from "lucide-react";
import { httpClient } from "../../HttpClient";
import { dialog } from "../../components/ui/Dialog";
import { showStatusToast } from "../../components/ui/Toast";

const API = "https://backend-myalfa.vercel.app/api";

/**
 * Banniere « Modifications de coordonnees en attente » dans la fiche client.
 *
 * Le client saisit ses nouvelles coordonnees dans MyAlfa Link ; la demande
 * est rangee en file d'attente cote Link (`myalfa_link.PendingClientUpdate`)
 * et un ToDo type `ModifCoordonneesClient` est cree au chargé de dossier.
 * Ici, on affiche ce qu'il y a a trancher, avec le diff ancien -> nouveau,
 * et deux boutons :
 *
 * - Appliquer : ecrit la valeur sur `workflow_client.Client` et clos le ToDo.
 * - Refuser : clos la demande sans modifier le dossier.
 *
 * Masquee quand il n'y a aucune demande en attente — pour ne pas ajouter du
 * bruit dans la fiche quand il n'y a rien a faire.
 */

const FIELD_LABELS = {
  raison_sociale: "Raison sociale",
  adresse: "Adresse",
  code_postal: "Code postal",
  ville: "Ville",
  tva_intracommunautaire: "N° TVA intracommunautaire",
  regime_tva: "Régime TVA",
};

const FIELD_ORDER = [
  "raison_sociale",
  "adresse",
  "code_postal",
  "ville",
  "tva_intracommunautaire",
  "regime_tva",
];

export default function PendingClientUpdateBanner({ clientId, onApplied }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [working, setWorking] = useState(null);

  const refresh = useCallback(async () => {
    if (!clientId) {
      return;
    }

    setLoading(true);

    try {
      const response = await httpClient.get(
        `${API}/pending-client-updates/clients/${clientId}`,
      );

      setRows(Array.isArray(response?.data) ? response.data : []);
    } catch (error) {
      // Silencieux : la fiche doit s'ouvrir meme si la route n'est pas la
      // (environnement de dev sans cette fonctionnalite).
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  if (loading || rows.length === 0) {
    return null;
  }

  const decide = async (row, verb) => {
    const message =
      verb === "apply"
        ? "La fiche du dossier va être mise à jour avec les nouvelles valeurs saisies par le client."
        : "La demande sera refusée et le client en sera informé.";

    const confirmed = await dialog.confirm(message, {
      variant: verb === "refuse" ? "danger" : undefined,
      confirmLabel: verb === "apply" ? "Appliquer" : "Refuser",
    });

    if (!confirmed) {
      return;
    }

    setWorking(row.id);

    const toast = showStatusToast({
      status: "loading",
      titre: verb === "apply" ? "Application…" : "Refus…",
      message: "",
    });

    try {
      await httpClient.post(
        `${API}/pending-client-updates/${row.id}/${verb}`,
      );

      toast.update({
        status: "success",
        titre:
          verb === "apply"
            ? "Modifications appliquées"
            : "Modifications refusées",
        message: "",
      });

      await refresh();

      if (verb === "apply" && typeof onApplied === "function") {
        onApplied();
      }
    } catch (error) {
      toast.update({
        status: "error",
        titre: "Opération impossible",
        message:
          error?.response?.data?.error ||
          "Une erreur est survenue. Réessayez dans un instant.",
      });
    } finally {
      setWorking(null);
    }
  };

  return (
    <div
      style={{
        borderRadius: 8,
        border: "1px solid #fcd34d",
        background: "#fffbeb",
        padding: 12,
        marginBottom: 12,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          color: "#92400e",
          fontWeight: 600,
          marginBottom: 8,
        }}
      >
        <AlertTriangle size={16} />
        {rows.length === 1
          ? "1 modification de coordonnées en attente"
          : `${rows.length} modifications de coordonnées en attente`}
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {rows.map((row) => (
          <PendingCard
            key={row.id}
            row={row}
            disabled={working !== null}
            onApply={() => decide(row, "apply")}
            onRefuse={() => decide(row, "refuse")}
          />
        ))}
      </div>
    </div>
  );
}

function PendingCard({ row, disabled, onApply, onRefuse }) {
  const diff = row.diff || {};
  const fields = FIELD_ORDER.filter((field) => diff[field]);
  const when = row.createdAt
    ? new Date(row.createdAt).toLocaleDateString("fr-FR", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "";

  return (
    <div
      style={{
        borderRadius: 6,
        background: "#fff",
        border: "1px solid #fde68a",
        padding: 10,
      }}
    >
      <div
        style={{
          fontSize: 12,
          color: "#92400e",
          marginBottom: 6,
        }}
      >
        Demandée par <strong>{row.requestedBy}</strong>
        {when ? ` le ${when}` : ""}
      </div>

      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          fontSize: 13,
          marginBottom: 8,
        }}
      >
        <thead>
          <tr style={{ textAlign: "left", color: "#64748b" }}>
            <th style={{ padding: "4px 6px", fontWeight: 600 }}>Champ</th>
            <th style={{ padding: "4px 6px", fontWeight: 600 }}>Avant</th>
            <th style={{ padding: "4px 6px", fontWeight: 600 }}>Demandé</th>
          </tr>
        </thead>
        <tbody>
          {fields.map((field) => (
            <tr key={field}>
              <td
                style={{
                  padding: "4px 6px",
                  borderTop: "1px solid #f1f5f9",
                  fontWeight: 500,
                }}
              >
                {FIELD_LABELS[field]}
              </td>
              <td
                style={{
                  padding: "4px 6px",
                  borderTop: "1px solid #f1f5f9",
                  color: "#64748b",
                }}
              >
                {diff[field]?.old ?? "—"}
              </td>
              <td
                style={{
                  padding: "4px 6px",
                  borderTop: "1px solid #f1f5f9",
                  color: "#0f172a",
                }}
              >
                {diff[field]?.new ?? "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <button
          type="button"
          onClick={onRefuse}
          disabled={disabled}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            padding: "6px 10px",
            borderRadius: 4,
            border: "1px solid #e5e7eb",
            background: "#fff",
            cursor: disabled ? "default" : "pointer",
            fontSize: 12,
          }}
        >
          <X size={12} />
          Refuser
        </button>
        <button
          type="button"
          onClick={onApply}
          disabled={disabled}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            padding: "6px 10px",
            borderRadius: 4,
            border: "1px solid #840040",
            background: "#840040",
            color: "#fff",
            cursor: disabled ? "default" : "pointer",
            fontSize: 12,
            fontWeight: 600,
          }}
        >
          <Check size={12} />
          Appliquer
        </button>
      </div>
    </div>
  );
}
